import React, { useState, useEffect, useRef } from "react";
import Popup from "../popup/Popup";
import { POPUP_FORM_CONTENT_CLASS, ACTION_POPUP_CONTENT_INNER_CLASS } from "../popup/popupLayoutSpec";
import { AccountPopupField } from "../BusinessDashboard/accountPopupFields";
import {
  AdminDetailFieldLabel,
  AdminFormFieldError,
  AdminHeaderAction,
  AdminPageTitle,
  AdminSupportingText,
  AdminMetadataText,
  adminWizardInputClasses,
  adminVerificationDigitClasses,
  adminVerificationCodeGroupClassNames,
} from "../../sections/AdminDashboard/shared/adminUx";

const LoginSecurityPopup = ({
  isOpen,
  onClose,
  setFieldValue,
  values = {},
  errors = {},
  initialStep = 0,
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formValues, setFormValues] = useState({});
  const [isVerifying, setIsVerifying] = useState(false);
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [otpVerificationField, setOtpVerificationField] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  const [timer, setTimer] = useState(120);
  const [showResend, setShowResend] = useState(false);

  useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);

  useEffect(() => {
    if (isOpen) {
      const sampleData = {
        email: "john@example.com",
        password: "",
        mobile: "0400 123 456",
      };
      setFormValues({
        email: values.email || sampleData.email,
        password: values.password || "",
        mobile: values.mobile || sampleData.mobile,
      });
      setShowOtpVerification(false);
      setShowConfirmation(false);
      setOtpVerificationField("");
      setOtp(["", "", "", "", "", ""]);
      setTimer(120);
      setShowResend(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && values) {
      const sampleData = {
        email: "john@example.com",
        password: "",
        mobile: "0400 123 456",
      };
      setFormValues((prev) => ({
        ...prev,
        email: values.email || sampleData.email,
        password: values.password || "",
        mobile: values.mobile || sampleData.mobile,
      }));
    }
  }, [values, isOpen]);

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  useEffect(() => {
    let interval = null;
    if (timer > 0 && showOtpVerification) {
      interval = setInterval(() => {
        setTimer((t) => t - 1);
      }, 1000);
    } else if (timer === 0) {
      setShowResend(true);
    }
    return () => clearInterval(interval);
  }, [timer, showOtpVerification]);

  const handleFieldChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);
    const nextEmptyIndex = newOtp.findIndex((digit) => digit === "");
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleResendOtp = () => {
    setTimer(120);
    setShowResend(false);
    setOtp(["", "", "", "", "", ""]);
  };

  const steps = [
    {
      title: "Update Email Address",
      field: "email",
      inputType: "email",
      placeholder: "Enter new email address",
      showCurrentPassword: false,
      needsVerification: true,
    },
    {
      title: "New Password",
      field: "password",
      inputType: "password",
      placeholder: "Enter new password",
      showCurrentPassword: true,
      needsVerification: true,
    },
    {
      title: "Update Mobile Number",
      field: "mobile",
      inputType: "tel",
      placeholder: "Enter new mobile number",
      showCurrentPassword: false,
      needsVerification: true,
    },
  ];

  const handleSave = async () => {
    if (currentStepData.needsVerification) {
      setOtpVerificationField(currentStepData.field);
      setShowOtpVerification(true);
      setTimer(120);
      setShowResend(false);
      setOtp(["", "", "", "", "", ""]);
    } else if (setFieldValue) {
      setFieldValue(currentStepData.field, formValues[currentStepData.field]);
      onClose();
    }
  };

  const handleOtpVerification = () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) return;
    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
      if (setFieldValue) {
        setFieldValue(otpVerificationField, formValues[otpVerificationField]);
      }
      setShowOtpVerification(false);
      setShowConfirmation(true);
      setTimeout(() => onClose(), 3000);
    }, 2000);
  };

  const currentStepData = steps[currentStep];

  if (!isOpen) return null;

  return (
    <Popup
      setTrigger={() => {}}
      ShowCrossButton={true}
      cross={true}
      onClose={onClose}
      businessDashboard={true}
      hideLogoInHeader={true}
    >
      <div
        className="overflow-auto relative flex items-center justify-center"
        style={{ minHeight: "calc(100vh - 120px)" }}
      >
        <div className={POPUP_FORM_CONTENT_CLASS}>
          <div className={ACTION_POPUP_CONTENT_INNER_CLASS}>
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="flex justify-center w-full">
                <div className="w-full mx-auto">
                  {showOtpVerification ? (
                    <div>
                      <div className="text-center mb-6">
                        <AdminPageTitle className="text-center pb-0 text-offblack mb-2">
                          Verify{" "}
                          {otpVerificationField === "email"
                            ? "Email"
                            : otpVerificationField === "mobile"
                              ? "Mobile Number"
                              : "Password Change"}
                        </AdminPageTitle>
                        <AdminSupportingText className="text-mediumgray block text-center">
                          {otpVerificationField === "password"
                            ? "We've sent a 6-digit verification code to your registered email address."
                            : `We've sent a 6-digit verification code to ${formValues[otpVerificationField]}`}
                        </AdminSupportingText>
                      </div>
                      <div className={`mb-4 ${adminVerificationCodeGroupClassNames(6)}`} onPaste={handlePaste}>
                        {otp.map((digit, index) => (
                          <input
                            key={index}
                            ref={(el) => {
                              inputRefs.current[index] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleOtpChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            className={adminVerificationDigitClasses()}
                            placeholder=""
                            aria-label={`Verification digit ${index + 1}`}
                          />
                        ))}
                      </div>
                      <div className="text-center mb-4">
                        {timer > 0 ? (
                          <AdminMetadataText className="text-mediumgray">
                            Resend code in {formatTime(timer)}
                          </AdminMetadataText>
                        ) : (
                          <button
                            type="button"
                            onClick={handleResendOtp}
                            className="text-sm text-primaryblue hover:text-brightblue"
                          >
                            Resend code
                          </button>
                        )}
                      </div>
                    </div>
                  ) : showConfirmation ? (
                    <div className="text-center">
                      <div className="mb-6">
                        <div className="w-16 h-16 bg-freshgreen rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="fi fi-rs-check text-white text-2xl" />
                        </div>
                        <AdminPageTitle className="text-center pb-0 text-offblack mb-2">
                          {otpVerificationField === "password"
                            ? "Password Updated!"
                            : otpVerificationField === "mobile"
                              ? "Mobile Number Updated!"
                              : otpVerificationField === "email"
                                ? "Email Updated!"
                                : "Information Updated!"}
                        </AdminPageTitle>
                        <AdminSupportingText className="text-mediumgray block text-center">
                          {otpVerificationField === "password"
                            ? "Your password has been successfully updated and is now secure."
                            : otpVerificationField === "mobile"
                              ? "Your mobile number has been successfully updated and verified."
                              : otpVerificationField === "email"
                                ? "Your email has been successfully updated and verified."
                                : "Your information has been successfully updated."}
                        </AdminSupportingText>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {currentStepData.showCurrentPassword ? (
                        <div className="space-y-1">
                          <AdminDetailFieldLabel htmlFor="currentPassword">
                            Current Password
                          </AdminDetailFieldLabel>
                          <input
                            id="currentPassword"
                            type="password"
                            name="currentPassword"
                            placeholder="Enter current password"
                            onChange={(e) => handleFieldChange("currentPassword", e.target.value)}
                            value={formValues.currentPassword || ""}
                            className={adminWizardInputClasses(Boolean(errors?.currentPassword))}
                          />
                          {errors?.currentPassword ? (
                            <AdminFormFieldError>{errors.currentPassword}</AdminFormFieldError>
                          ) : null}
                        </div>
                      ) : null}
                      <AccountPopupField
                        step={{
                          title: currentStepData.title,
                          field: currentStepData.field,
                          inputType: currentStepData.inputType,
                          placeholder: currentStepData.placeholder,
                        }}
                        fieldKey={currentStepData.field}
                        value={formValues[currentStepData.field] || ""}
                        onChange={(v) => handleFieldChange(currentStepData.field, v)}
                        form={{ touched: {}, errors }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {!showConfirmation && (
                <div className="flex justify-end w-full">
                  <div className="flex items-center justify-end mt-10 w-full">
                    <AdminHeaderAction
                      type="button"
                      onClick={showOtpVerification ? handleOtpVerification : handleSave}
                      disabled={isVerifying || (showOtpVerification && otp.join("").length !== 6)}
                    >
                      {isVerifying ? "Verifying" : showOtpVerification ? "Verify" : "Save"}
                    </AdminHeaderAction>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default LoginSecurityPopup;
